/**
 * Dashboard, weekly report, and demo-data services.
 * @module services/dashboardService
 */
import { api } from "../lib/api";

export const dashboardService = {
  /** @returns {Promise<import("./types").DashboardSnapshot>} */
  get: () => api.get("/dashboard").then((r) => r.data),

  /**
   * Period-aware report.
   * @param {"weekly"|"monthly"} period
   * @param {string|null} start  ISO datetime anchor
   */
  report: (period = "weekly", start = null) => {
    const params = { period };
    if (start) params.start = start;
    return api.get("/report", { params }).then((r) => r.data);
  },

  /** Legacy weekly endpoint kept for the PDF export path. */
  weeklyReport: () => api.get("/report/weekly").then((r) => r.data),

  /**
   * Daily net-worth time series for the past `days` days (default 90).
   * @param {number} days  7-365
   * @param {string|null} currency  Filter to a single snapshot_currency basis.
   */
  netWorthSeries: (days = 90, currency = null) => {
    const params = { days };
    if (currency) params.currency = currency;
    return api.get("/dashboard/net-worth-series", { params }).then((r) => r.data);
  },

  /**
   * Forward-looking daily cash forecast for the next `days` days (default 30).
   * @param {number} days  7-180
   */
  forecast: (days = 30) =>
    api.get("/dashboard/forecast", { params: { days } }).then((r) => r.data),

  /**
   * Historical movements / collected receivables / paid payments for a range.
   * Defaults to current calendar month if no range provided.
   * @param {string|null} start  ISO datetime (inclusive)
   * @param {string|null} end    ISO datetime (exclusive)
   */
  history: (start = null, end = null) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return api.get("/dashboard/history", { params }).then((r) => r.data);
  },

  /**
   * FX gain/loss for the user's FX-tagged movements in [start, end).
   * Defaults to the current calendar month. Returns totals + per-currency
   * breakdown + biggest-swing items list. See services/fx_pnl.py.
   */
  fxPnl: (start = null, end = null) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return api.get("/dashboard/fx-pnl", { params }).then((r) => r.data);
  },
};

export const remindersService = {
  /** What today's reminder would look like (no side-effects). */
  preview: () => api.get("/reminders/preview").then((r) => r.data),
  /** Send today's reminder to every linked channel (force). */
  sendNow: () => api.post("/reminders/send-now").then((r) => r.data),
  /** Most recently sent reminder for the current user. */
  last: () => api.get("/reminders/last").then((r) => r.data),
};

export const fxService = {
  /** USD-base rates + freshness metadata (cached server-side 24h). */
  rates: () => api.get("/fx/rates").then((r) => r.data),
  /** Re-stamp the FX rate on every foreign-currency record. */
  resnapshot: (scope = "all") => api.post("/fx/resnapshot", null, { params: { scope } }).then((r) => r.data),
};

export const demoService = {
  /** @returns {Promise<{ok:true,message:string}>} */
  seed: () => api.post("/seed-demo").then((r) => r.data),

  /** @returns {Promise<{ok:true,message:string}>} */
  clear: () => api.delete("/seed-demo").then((r) => r.data),
};
